import { Menu, X, Globe, ChevronRight, Home } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../assets/images/Cialda-Logo.png';
import { useLanguageContext } from "../context/languageContext";
import LanguageSelector from "../translation/LanguageSelector";
import { telHref } from '../utils/telHref';

interface HeaderProps {
  showMenu?: boolean;
  activeSection?: string;
  setActiveSection?: (section: string) => void;
}

export default function BusinessHeader({ showMenu = false, activeSection, setActiveSection }: HeaderProps) {
  const { t } = useLanguageContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageExpanded, setLanguageExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = () => {
    if (location.pathname !== '/home' && location.pathname !== '/') {
      navigate('/home');
    } else {
      scrollToSection('hero');
    }
  }

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/home' && location.pathname !== '/') {
      navigate('/home');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const offset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 500);
      return;
    }

    if (setActiveSection) {
      setActiveSection(sectionId);
    }
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const contactNumber = '+355 69 205 5470';

  const menuItems = [
    { id: 'menu', label: 'Menu' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'private-celebrations', label: 'Events' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? 'bg-white shadow-sm py-2'
          : 'bg-gradient-to-b from-black/60 to-transparent py-6'
          }`}
      >
        <div className="container mx-auto px-4 md:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center group cursor-pointer" onClick={handleLogoClick}>
              <img
                src={Logo}
                alt="Cialda Logo"
                className={`transition-all duration-300 ${scrolled ? 'h-10 md:h-12' : 'h-14 md:h-20'}`}
              />
            </div>

            {/* Desktop Navigation */}
            {showMenu && (
              <nav className="hidden md:flex items-center gap-10">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`text-sm font-bold uppercase tracking-widest transition-colors duration-300 hover:text-gold-deep ${scrolled
                      ? (activeSection === item.id ? 'text-gold-deep' : 'text-dark-chocolate')
                      : (activeSection === item.id ? 'text-gold' : 'text-white')
                      }`}
                  >
                    {t(item.label)}
                  </button>
                ))}
              </nav>
            )}

            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              {/* Home Button */}
              <button
                onClick={() => navigate('/home')}
                className={`p-2 transition-colors duration-300 hover:text-gold-deep ${scrolled ? 'text-dark-chocolate' : 'text-white'}`}
                title={t("Home")}
              >
                <Home className="w-5 h-5" />
              </button>

              {/* Language Selector */}
              <div className="relative">
                {languageExpanded ? (
                  <div className="absolute top-12 right-0 bg-white rounded-lg shadow-xl p-4 border border-gray-100 min-w-[200px]">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t("Select Language")}</span>
                      <button onClick={() => setLanguageExpanded(false)}>
                        <X className="w-4 h-4 text-gray-400 hover:text-black" />
                      </button>
                    </div>
                    <LanguageSelector />
                  </div>
                ) : (
                  <button
                    onClick={() => setLanguageExpanded(true)}
                    className={`p-2 transition-colors duration-300 hover:text-gold-deep ${scrolled ? 'text-dark-chocolate' : 'text-white'}`}
                  >
                    <Globe className="w-5 h-5" />
                  </button>
                )}
              </div>



              {/* Mobile Menu Toggle */}
              <div className="md:hidden">
                <button
                  className={`p-2 transition-colors duration-300 ${scrolled ? 'text-dark-chocolate' : 'text-white'}`}
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <Menu className="w-7 h-7" />
                </button>
              </div>
            </div>


          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity duration-300 md:hidden ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Drawer Panel */}
      <div className={`fixed top-0 right-0 h-full w-[85%] max-w-[300px] bg-cream shadow-2xl z-[70] transform transition-transform duration-300 md:hidden flex flex-col ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Drawer Header */}
        <div className="flex items-center justify-between p-6 border-b border-gold/10">
          <span className="text-dark-chocolate font-serif font-bold text-xl">{t("Explore")}</span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 bg-white rounded-full text-dark-chocolate hover:bg-gold hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Content (Scrollable) */}
        <div className="flex-1 overflow-y-auto py-6 px-6">
          <div className="space-y-6">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="w-full flex items-center justify-between text-left group"
              >
                <span className={`text-lg font-serif font-medium transition-colors ${activeSection === item.id ? 'text-gold-deep' : 'text-dark-chocolate group-hover:text-gold-deep'}`}>
                  {t(item.label)}
                </span>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gold-deep transition-colors" />
              </button>
            ))}
          </div>

          <div className="my-8 h-[1px] bg-gold/10" />

          {/* Integrated Language Selector (Mobile Variant) */}
          <div className="space-y-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t("Language")}</p>
            <div className="bg-white/50 rounded-lg border border-gold/10 p-2">
              <LanguageSelector variant="mobile" />
            </div>
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="p-6 bg-white border-t border-gold/10">
          <a
            href={telHref(contactNumber)}
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full py-3 text-center bg-dark-chocolate text-white text-sm font-bold uppercase tracking-widest rounded-xl hover:bg-gold hover:text-dark-chocolate transition-colors"
          >
            {t("Contact Us")}
          </a>
        </div>
      </div>



    </>
  );
}
